import fetcher from 'api/fetcher';
import { MagritteNotificationName } from 'utils/constants/notification';

import { pluralize } from '@hh.ru/front-static-app';

import { translate } from 'hooks/translation/useTrls';

import { setCampaignsOrRefetchEmptyPage } from 'components/CampaignsDashboard/utils/setCampaignsOrRefetchEmptyPage';

import { CampaignFrontendStatus, CampaignsTab, setCheckedCampaignIds } from 'store/models/campaignsDashboard';
import {
    addMagritteCustomNotification,
    addMagritteNotification,
    MagritteNotificationType,
} from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

export interface MultiStatusUpdate {
    updatedCampaignIds: Array<number>;
    failedCampaignIds: Array<number>;
}

const TrlKeys = {
    stopped: {
        one: 'CampaignsMultiActionsPanel.notification.multiStopSuccess.stopped.one',
        many: 'CampaignsMultiActionsPanel.notification.multiStopSuccess.stopped.many',
    },
    campaigns: {
        one: 'CampaignsMultiActionsPanel.notification.multiStopSuccess.campaigns.one',
        some: 'CampaignsMultiActionsPanel.notification.multiStopSuccess.campaigns.some',
        many: 'CampaignsMultiActionsPanel.notification.multiStopSuccess.campaigns.many',
    },
};

export const fetchMultiStop =
    (checkedCampaignIds: Set<number>, abortSignal: AbortSignal): AppThunkAction =>
    async (dispatch, getState) => {
        const trls = getState().translations.trls;
        const campaigns = getState().campaignsDashboard.campaigns;
        const campaignsCurrentTab = getState().campaignsDashboard.campaignsCurrentTab;
        const shouldRemoveFromList = [CampaignsTab.Active, CampaignsTab.Attention].includes(campaignsCurrentTab);

        return fetcher
            .put<unknown, MultiStatusUpdate>(
                '/api/v1/campaigns/status/batch',
                {
                    campaignIds: [...checkedCampaignIds],
                    newStatus: CampaignFrontendStatus.Blocked,
                },
                {
                    signal: abortSignal,
                    timeout: 30000,
                }
            )
            .then(({ updatedCampaignIds, failedCampaignIds }) => {
                const newCheckedCampaignIds = new Set(failedCampaignIds);
                const newCampaignsList = new Map(campaigns);
                const updatedIds = new Set(updatedCampaignIds);

                updatedIds.forEach((id) => {
                    if (shouldRemoveFromList) {
                        newCampaignsList.delete(id);
                        return;
                    }

                    const campaign = newCampaignsList.get(id);
                    if (campaign) {
                        newCampaignsList.set(id, {
                            ...campaign,
                            frontendStatus: CampaignFrontendStatus.Blocked,
                        });
                    }
                });

                if (failedCampaignIds.length > 0) {
                    dispatch(addMagritteNotification(MagritteNotificationName.CampaignMultiChangeStatusToBlockedError));
                } else {
                    dispatch(
                        addMagritteCustomNotification({
                            type: MagritteNotificationType.Success,
                            text: `${pluralize(updatedIds.size, {
                                zero: translate(trls, TrlKeys.stopped.many),
                                one: translate(trls, TrlKeys.stopped.one),
                                some: translate(trls, TrlKeys.stopped.many),
                                many: translate(trls, TrlKeys.stopped.many),
                                hasValue: false,
                            })} ${pluralize(updatedIds.size, {
                                zero: translate(trls, TrlKeys.campaigns.many),
                                one: translate(trls, TrlKeys.campaigns.one),
                                some: translate(trls, TrlKeys.campaigns.some),
                                many: translate(trls, TrlKeys.campaigns.many),
                            })}`,
                        })
                    );
                }

                dispatch(setCampaignsOrRefetchEmptyPage(newCampaignsList, abortSignal));
                dispatch(setCheckedCampaignIds(newCheckedCampaignIds));
            })
            .catch(() => {
                if (abortSignal.aborted) {
                    return;
                }

                dispatch(addMagritteNotification(MagritteNotificationName.CampaignMultiChangeStatusToBlockedError));
            });
    };
